import { IconChevronRight } from '@tabler/icons-react'
import { ButtonHTMLAttributes, ReactNode, useId, useState } from 'react'

import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { astraTwMerge } from '../Utilities/astraTwMerge'

export type AccordionItemProps<T extends string> = {
    value: T
    title: string
    content: ReactNode
    afterTitle?: ReactNode
    triggerIcon?: ReactNode
    hide?: boolean
    overflow?: boolean
}

type AccordionItemComponentProps<T extends string> = {
    item: AccordionItemProps<T>
    isOpen: boolean
    itemSeparator: 'gap' | 'line'
    stickyHeaders: boolean
    onToggle: () => void
}

const AccordionItem = <T extends string>({ item, isOpen, onToggle, itemSeparator, stickyHeaders }: AccordionItemComponentProps<T>) => {
    const id = useId()
    const triggerId = `accordion-trigger-${id}`
    const contentId = `accordion-content-${id}`
    const state = isOpen ? 'open' : 'closed'

    if (item.hide) {
        return null
    }

    return (
        <div className={astraTwMerge('flex flex-col gap-2', itemSeparator === 'line' ? 'p-4' : '', item.overflow && !stickyHeaders ? 'overflow-auto' : '')}>
            <div className={astraTwMerge('flex items-center gap-2', stickyHeaders ? 'sticky top-0 z-elevated bg-surface-base' : '')}>
                <div id={triggerId} data-state={state} className="group">
                    <AstraButton
                        variant="ghost"
                        icon={
                            <div className="transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90">
                                <AstraIcon icon={IconChevronRight} aria-hidden />
                            </div>
                        }
                        label={item.title}
                        endIcon={item.triggerIcon}
                        tightPadding
                        onClick={onToggle}
                        {...({ 'aria-expanded': isOpen, 'aria-controls': contentId } as ButtonHTMLAttributes<HTMLButtonElement>)}
                    />
                </div>
                {item.afterTitle}
            </div>
            <div
                id={contentId}
                role="region"
                aria-labelledby={triggerId}
                hidden={!isOpen}
                className={astraTwMerge('pl-2', stickyHeaders ? 'isolate' : '', item.overflow && !stickyHeaders ? 'overflow-auto' : '')}
            >
                {item.content}
            </div>
        </div>
    )
}

type Props<T extends string> = {
    items: AccordionItemProps<T>[]
    itemSeparator?: 'gap' | 'line'
    defaultOpen?: Array<T>
    value?: Array<T>
    /** Stick section headers to the nearest scrolling ancestor. Disables internal scrolling, including item overflow. */
    stickyHeaders?: boolean
    onValueChange?: (value: Array<T>) => void
}

/**
 * Accordion stacks titled sections that expand and collapse independently, so a long page can show its
 * headings first and reveal detail on demand. Use it for grouped secondary content in a panel or drawer. Use
 * Tabs when only one section should be visible at a time, and Collapsible Card
 * when a single card needs to fold away.
 *
 * Open state is uncontrolled via `defaultOpen`, or controlled via `value` and `onValueChange`.
 *
 * @summary for stacked sections that expand and collapse independently
 */
export const AstraAccordion = <T extends string>({ items, itemSeparator = 'line', defaultOpen, value, onValueChange, stickyHeaders = false }: Props<T>) => {
    const [internalOpen, setInternalOpen] = useState<Array<T>>(defaultOpen ?? [])
    const isControlled = value !== undefined
    const openItems = isControlled ? value : internalOpen

    const toggleItem = (itemValue: T) => {
        const newOpen = openItems.includes(itemValue) ? openItems.filter(v => v !== itemValue) : [...openItems, itemValue]

        if (isControlled) {
            onValueChange?.(newOpen)
        } else {
            setInternalOpen(newOpen)
            onValueChange?.(newOpen)
        }
    }

    return (
        <div
            data-test="hero-accordion"
            className={astraTwMerge('flex flex-col', stickyHeaders ? '' : 'overflow-auto', itemSeparator === 'line' ? 'divide-y divide-base' : 'gap-6')}
        >
            {items.map((item, index) => (
                <AccordionItem
                    key={index}
                    item={item}
                    isOpen={openItems.includes(item.value)}
                    onToggle={() => toggleItem(item.value)}
                    itemSeparator={itemSeparator}
                    stickyHeaders={stickyHeaders}
                />
            ))}
        </div>
    )
}
